import React, { useState, useEffect } from 'react';
import { targetCompanyService } from '../services/targetCompanyService';

export default function TargetCompanies() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [total, setTotal] = useState(0);
  const [limit] = useState(50);
  const [offset, setOffset] = useState(0);
  const [search, setSearch] = useState('');
  const [isPausedFilter, setIsPausedFilter] = useState(null);
  const [confidenceFilter, setConfidenceFilter] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [actionLoading, setActionLoading] = useState({});

  // Form state for add/edit
  const [formData, setFormData] = useState({
    display_name: '',
    legal_name: '',
    website: '',
    root_domain: '',
    industry: '',
    city: '',
    country: ''
  });

  useEffect(() => {
    loadCompanies();
  }, [offset, search, isPausedFilter, confidenceFilter]);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        limit,
        offset,
        ...(search && { search }),
        ...(isPausedFilter !== null && { is_paused: isPausedFilter }),
        ...(confidenceFilter && { target_confidence: confidenceFilter })
      };
      const data = await targetCompanyService.getTargetCompanies(params);
      setCompanies(data.companies || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to load target companies:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load target companies');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCompany = async () => {
    try {
      setActionLoading({ add: true });
      await targetCompanyService.addTargetCompany(formData);
      setShowAddModal(false);
      resetForm();
      loadCompanies();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to add company');
    } finally {
      setActionLoading({ add: false });
    }
  };

  const handleUpdateCompany = async (companyId, updates) => {
    try {
      setActionLoading({ [companyId]: true });
      await targetCompanyService.updateTargetCompany(companyId, updates);
      setEditingCompany(null);
      loadCompanies();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to update company');
    } finally {
      setActionLoading({ [companyId]: false });
    }
  };

  const handleRemoveCompany = async (companyId) => {
    if (!window.confirm('Are you sure you want to remove this company from the target list?')) {
      return;
    }
    try {
      setActionLoading({ [companyId]: true });
      await targetCompanyService.removeTargetCompany(companyId);
      loadCompanies();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to remove company');
    } finally {
      setActionLoading({ [companyId]: false });
    }
  };

  const handleTogglePause = async (company) => {
    await handleUpdateCompany(company.company_id, { is_paused: !company.is_paused });
  };

  const resetForm = () => {
    setFormData({
      display_name: '',
      legal_name: '',
      website: '',
      root_domain: '',
      industry: '',
      city: '',
      country: ''
    });
  };

  const extractDomain = (website) => {
    if (!website) return '';
    try {
      const url = new URL(website.startsWith('http') ? website : `https://${website}`);
      return url.hostname.replace('www.', '');
    } catch {
      return '';
    }
  };

  const handleWebsiteChange = (value) => {
    setFormData(prev => ({
      ...prev,
      website: value,
      root_domain: prev.root_domain || extractDomain(value)
    }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Target Companies</h2>
            <p className="text-sm text-gray-500 mt-1">
              Manage companies that are targets for signal processing and outreach
            </p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
            className="px-4 py-2 bg-[#00C6AE] text-white rounded-lg hover:bg-[#00B39E] transition-colors"
          >
            + Add Company
          </button>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
          <div>
            <input
              type="text"
              placeholder="Search companies..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setOffset(0);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00C6AE] focus:border-transparent"
            />
          </div>
          <div>
            <select
              value={isPausedFilter === null ? '' : isPausedFilter.toString()}
              onChange={(e) => {
                const value = e.target.value === '' ? null : e.target.value === 'true';
                setIsPausedFilter(value);
                setOffset(0);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00C6AE] focus:border-transparent"
            >
              <option value="">All Status</option>
              <option value="false">Active</option>
              <option value="true">Paused</option>
            </select>
          </div>
          <div>
            <select
              value={confidenceFilter}
              onChange={(e) => {
                setConfidenceFilter(e.target.value);
                setOffset(0);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00C6AE] focus:border-transparent"
            >
              <option value="">All Confidence</option>
              <option value="derived">Derived</option>
              <option value="manual">Manual</option>
            </select>
          </div>
          <div className="text-sm text-gray-500 flex items-center">
            Total: {total} companies
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {/* Companies Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#00C6AE]"></div>
            <p className="mt-2 text-gray-500">Loading companies...</p>
          </div>
        ) : companies.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">No target companies found</p>
            {search && (
              <button
                onClick={() => setSearch('')}
                className="mt-2 text-[#00C6AE] hover:underline"
              >
                Clear search
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Company Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Domain
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Industry
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Confidence
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {companies.map((company) => (
                    <tr key={company.company_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {company.display_name}
                        </div>
                        {company.legal_name && (
                          <div className="text-sm text-gray-500">{company.legal_name}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {company.root_domain || '-'}
                        </div>
                        {company.website && (
                          <a
                            href={company.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-[#00C6AE] hover:underline"
                          >
                            Visit →
                          </a>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {company.industry || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {[company.city, company.country].filter(Boolean).join(', ') || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            company.target_confidence === 'manual'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {company.target_confidence === 'manual' ? 'Manual' : 'Derived'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {company.is_paused ? (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                            Paused
                          </span>
                        ) : (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            Active
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleTogglePause(company)}
                            disabled={actionLoading[company.company_id]}
                            className="text-[#00C6AE] hover:text-[#00B39E] disabled:opacity-50"
                            title={company.is_paused ? 'Resume' : 'Pause'}
                          >
                            {company.is_paused ? '▶' : '⏸'}
                          </button>
                          <button
                            onClick={() => handleRemoveCompany(company.company_id)}
                            disabled={actionLoading[company.company_id]}
                            className="text-red-600 hover:text-red-700 disabled:opacity-50"
                            title="Remove from targets"
                          >
                            ✕
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {total > limit && (
              <div className="bg-gray-50 px-6 py-3 flex items-center justify-between border-t border-gray-200">
                <div className="text-sm text-gray-500">
                  Showing {offset + 1} to {Math.min(offset + limit, total)} of {total} companies
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setOffset(Math.max(0, offset - limit))}
                    disabled={offset === 0}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setOffset(offset + limit)}
                    disabled={offset + limit >= total}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add Company Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Add Target Company</h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.display_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00C6AE] focus:border-transparent"
                  placeholder="e.g., Acme Corporation"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Legal Name
                </label>
                <input
                  type="text"
                  value={formData.legal_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, legal_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00C6AE] focus:border-transparent"
                  placeholder="e.g., Acme Corporation Pte. Ltd."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Website
                </label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => handleWebsiteChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00C6AE] focus:border-transparent"
                  placeholder="https://www.example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Root Domain
                </label>
                <input
                  type="text"
                  value={formData.root_domain}
                  onChange={(e) => setFormData(prev => ({ ...prev, root_domain: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00C6AE] focus:border-transparent"
                  placeholder="example.com"
                />
                <p className="text-xs text-gray-500 mt-1">Auto-extracted from website if not provided</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Industry
                  </label>
                  <input
                    type="text"
                    value={formData.industry}
                    onChange={(e) => setFormData(prev => ({ ...prev, industry: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00C6AE] focus:border-transparent"
                    placeholder="e.g., Technology"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00C6AE] focus:border-transparent"
                    placeholder="e.g., Singapore"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country
                </label>
                <input
                  type="text"
                  value={formData.country}
                  onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00C6AE] focus:border-transparent"
                  placeholder="e.g., Singapore"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleAddCompany}
                disabled={!formData.display_name || actionLoading.add}
                className="px-4 py-2 bg-[#00C6AE] text-white rounded-lg hover:bg-[#00B39E] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {actionLoading.add ? 'Adding...' : 'Add Company'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
